"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Calendar, User } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12">
        <div className="h-12 w-3/4 animate-pulse rounded-md bg-muted" />
        <div className="mt-8 h-96 w-full animate-pulse rounded-lg bg-muted" />
        <div className="mt-8 space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 text-center">
        <p className="text-muted-foreground">No se pudo cargar la publicación.</p>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-4xl px-4 py-12">
      <header className="mb-8">
        <h1 className="text-4xl font-bold leading-tight text-foreground md:text-5xl">
          {post.title}
        </h1>
        
        <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("es-ES", {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}
              </time>
            </div>
          )}
          
          {post.author && (
            <div className="flex items-center gap-2">
              <User className="size-4" />
              <span>{post.author.name}</span>
            </div>
          )}
        </div>

        {post.categories && post.categories.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {post.categories.map((category) => (
              <span
                key={category.id}
                className="rounded-md bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
              >
                {category.name}
              </span>
            ))}
          </div>
        )}
      </header>

      {post.featuredImage && (
        <div className="mb-10 overflow-hidden rounded-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full object-cover"
          />
        </div>
      )}

      {post.content && (
        <div 
          className="prose prose-lg max-w-none text-foreground prose-headings:font-display prose-headings:text-foreground prose-p:text-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      <Separator className="my-12" />

      {post.author && (
        <div className="rounded-lg bg-muted-light-background p-6">
          <div className="flex items-start gap-4">
            <Avatar className="size-16">
              {post.author.avatar?.["96"] && (
                <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
              )}
              <AvatarFallback className="bg-primary/10 text-primary">
                {post.author.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1">
              <h3 className="font-display text-lg font-semibold text-foreground">
                Sobre el autor
              </h3>
              <p className="mt-1 font-semibold text-foreground">{post.author.name}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Coach personal dedicado a acompañar a quienes sienten que están estancados.
              </p>
            </div>
          </div>
        </div>
      )}

      {post.tags && post.tags.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-3 font-display text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Etiquetas
          </h3>
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <span
                key={tag.id}
                className="rounded-md border border-border bg-background px-3 py-1 text-sm text-foreground transition-colors hover:bg-accent"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-12 text-center">
        <Link
          to="/HomePage"
          className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
        >
          ← Volver al inicio
        </Link>
      </div>
    </article>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = wvcClient.getCurrentPostID();

  return (
    <PostsProvider postType="post" wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
      <SinglePostContent />
    </PostsProvider>
  );
}